import { debugError, debugWarn } from "@/utils/debug";
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    type ReactNode,
} from "react";

export interface Command {
    name: string;
    execute: (payload?: unknown) => void | Promise<void>;
}

interface CommandRegistryValue {
    run: (name: string, payload?: unknown) => Promise<void>;
    has: (name: string) => boolean;
    register: (commands: Command[]) => () => void;
}

const CommandRegistryContext = createContext<CommandRegistryValue | null>(null);

export interface CommandRegistryProviderProps {
    children: ReactNode;
}

export function CommandRegistryProvider({
    children,
}: CommandRegistryProviderProps) {
    const commandsRef = useRef<Map<string, Command>>(new Map());

    const value = useMemo<CommandRegistryValue>(() => {
        const run = async (name: string, payload?: unknown) => {
            const command = commandsRef.current.get(name);

            if (!command) {
                debugError(`[CommandRegistry] command not found: ${name}`);
                return;
            }

            try {
                await command.execute(payload);
            } catch (error) {
                debugError(`[CommandRegistry] command '${name}' threw`, error);
            }
        };

        const has = (name: string) => commandsRef.current.has(name);

        const register = (commands: Command[]) => {
            if (!commands || commands.length === 0) {
                return () => undefined;
            }

            const registeredEntries: Array<[string, Command]> = [];

            for (const command of commands) {
                const existing = commandsRef.current.get(command.name);

                if (existing && existing !== command) {
                    debugWarn(
                        `[CommandRegistry] duplicate command registration: ${command.name}`
                    );
                }

                commandsRef.current.set(command.name, command);
                registeredEntries.push([command.name, command]);
            }

            return () => {
                for (const [name, command] of registeredEntries) {
                    const current = commandsRef.current.get(name);

                    // Remove only if this exact registration is still active.
                    // This prevents an older cleanup from deleting a newer
                    // registration for the same command name.
                    if (current === command) {
                        commandsRef.current.delete(name);
                    }
                }
            };
        };

        return { run, has, register };
    }, []);

    return (
        <CommandRegistryContext.Provider value={value}>
            {children}
        </CommandRegistryContext.Provider>
    );
}

export function useCommandRegistry(): CommandRegistryValue {
    const context = useContext(CommandRegistryContext);

    if (!context) {
        throw new Error(
            "useCommandRegistry must be used within a CommandRegistryProvider"
        );
    }

    return context;
}

export function useRunCommand() {
    return useCommandRegistry().run;
}

export function useRegisterCommands(commands: Command[]) {
    const { register } = useCommandRegistry();

    useEffect(() => {
        if (!commands || commands.length === 0) {
            return;
        }

        return register(commands);
    }, [register, commands]);
}